import { ArrowBigDown, Fullscreen, Maximize } from "lucide-react";
import { RefObject, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

// Extended interface to include browser-specific fullscreen methods
interface FullscreenHTMLElement extends HTMLElement {
  mozRequestFullScreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

// Extended interface for document to include browser-specific fullscreen methods
interface ExtendedDocument extends Document {
  mozCancelFullScreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
  fullscreenElement: Element | null;
  mozFullScreenElement: Element | null;
  webkitFullscreenElement: Element | null;
  msFullscreenElement: Element | null;
}

interface FullScreenActionButtonProps {
  onclickRef: RefObject<HTMLDivElement>;
}

export const FullScreenActionButton = ({
  onclickRef,
}: FullScreenActionButtonProps): React.JSX.Element => {
  const checkFullscreen = (): boolean => {
    const doc = document as ExtendedDocument;
    const fullscreenElement =
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement;

    return !!fullscreenElement;
  };
  const [isFullScreen, setIsFullScreen] = useState(checkFullscreen());

  const handleFullScreen = async (): Promise<void> => {
    const element = document.documentElement as FullscreenHTMLElement;
    const doc = document as ExtendedDocument;
    try {
      if (!isFullScreen) {
        if (element.requestFullscreen) {
          // request going fullscreen
          await element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
          // for mozilla
          await element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
          // for chrome and safari and opera
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          // for IE/Edge
          await element.msRequestFullscreen();
        }
        localStorage.setItem("isFullScreen", "true");
      } else {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
        localStorage.setItem("isFullScreen", "false");
      }
    } catch (e) {
      localStorage.setItem("isFullScreen", "false");
    }
  };

  const handleFullScreenChange = (): void => {
    const doc = document as ExtendedDocument;
    setIsFullScreen(!!doc.fullscreenElement);
  };

  useEffect(() => {
    const doc = document as ExtendedDocument;

    const fullscreenEvents = [
      "fullscreenchange",
      "webkitfullscreenchange",
      "mozfullscreenchange",
      "MSFullscreenChange",
    ];

    fullscreenEvents.forEach((event) => {
      doc.addEventListener(event, handleFullScreenChange);
    });

    return (): void => {
      fullscreenEvents.forEach((event) => {
        doc.removeEventListener(event, handleFullScreenChange);
      });
    };
  }, []);

  useEffect(() => {
    if (onclickRef.current) {
      onclickRef.current.addEventListener("click", handleFullScreen);
    }
  }, [onclickRef, handleFullScreen, handleFullScreenChange]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          className='w-full pl-1 flex justify-start gap-4 transition-all duration-300 ease-in-out hover:text-primary [&[data-state=open]]:pointer-events-none shrink-0'
          variant='ghost'
          size='icon'
          // onClick={handleFullScreen}
        >
          {isFullScreen ? (
            <Maximize className='size-5 3xl:size-5 text-aldgray' />
          ) : (
            <Fullscreen className='size-5 3xl:size-5 text-aldgray' />
          )}
          <p className='text-aldgray font-normal'>
            {isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
          </p>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <ArrowBigDown width={11} height={5} className='fill-primary' />
        <p className='text-xs'>
          {isFullScreen ? "Enter Full Screen" : "Exit Full Screen"}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};
